
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/MyContext';
import { CourseModule, getContentByAccessLevel } from '@/data/courseContent';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { ChevronRight, File, FileText, Video, Lock, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';

const CourseContent: React.FC = () => {
  const { user, checkMembership } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({});
  const [accessibleModules, setAccessibleModules] = useState<CourseModule[]>([]);
  const [lastMembershipsString, setLastMembershipsString] = useState("");

  // Updated function to load content that can be called from multiple places
  const loadAccessibleContent = useCallback(() => {
    if (!user) {
      setAccessibleModules([]);
      return;
    }
    
    // Get the current user data directly from localStorage to ensure freshness
    const allUsers = JSON.parse(localStorage.getItem("allUsers") || "[]");
    const currentUser = allUsers.find((u: any) => u.email === user.email);
    
    if (!currentUser) {
      console.log("Could not find current user in localStorage");
      return;
    }
    
    const currentMemberships = currentUser.memberships || [];
    const currentMembershipsString = JSON.stringify(currentMemberships);
    
    // Only reload if memberships have changed
    if (currentMembershipsString !== lastMembershipsString) {
      console.log("User memberships changed, refreshing content access", user);
      
      if (currentUser.isAdmin || currentMemberships.includes('all')) {
        setAccessibleModules(getContentByAccessLevel('all'));
      } else {
        const modules: CourseModule[] = [];
        
        currentMemberships.forEach(membership => {
          const content = getContentByAccessLevel(membership);
          
          content.forEach(module => {
            if (!modules.some(m => m.id === module.id)) {
              modules.push(module);
            }
          });
        });
        
        setAccessibleModules(modules);
      }
      
      setLastMembershipsString(currentMembershipsString);
    }
  }, []);

  // Initial load and when user changes
  // useEffect(() => {
  //   loadAccessibleContent();
  // }, []);

  // Set up more frequent polling for membership changes
  // useEffect(() => {
  //   if (user) {
  //     const pollInterval = setInterval(() => {
  //       loadAccessibleContent();
  //     }, 500); // Check every 500ms for changes
      
  //     return () => clearInterval(pollInterval);
  //   }
  // }, [user, loadAccessibleContent]);

  const toggleModule = (moduleId: string) => {
    setOpenModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleContentClick = (moduleId: string, itemId: string, accessLevel: string) => {
    if (checkMembership(accessLevel)) {
      navigate(`/course/${moduleId}/${itemId}`);
    } else {
      toast({
        title: "Access Restricted",
        description: "You need to upgrade your membership to access this content.",
        variant: "destructive"
      });
    }
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className="h-4 w-4" />;
      case 'pdf':
        return <FileText className="h-4 w-4" />;
      case 'audio':
        return <File className="h-4 w-4" />;
      default:
        return <File className="h-4 w-4" />;
    }
  };

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Course Content</CardTitle>
          <CardDescription>Please login to view course content</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (accessibleModules.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Content Available</CardTitle>
          <CardDescription>You don't have access to any content yet. Please purchase a membership.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => navigate('/pricing')}>View Membership Options</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Course Content</CardTitle>
        <CardDescription>Access your course materials below</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="modules" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="modules">By Module</TabsTrigger>
            <TabsTrigger value="type">By Type</TabsTrigger>
          </TabsList>
          
          <TabsContent value="modules">
            <div className="space-y-2">
              {accessibleModules.map((module: CourseModule) => (
                <Collapsible
                  key={module.id}
                  open={openModules[module.id]}
                  onOpenChange={() => toggleModule(module.id)}
                  className="border rounded-md"
                >
                  <CollapsibleTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex w-full justify-between p-4 h-auto"
                    >
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-left">{module.title}</span>
                        <span className="text-xs text-gray-500 text-left">{module.items.length} items</span>
                      </div>
                      <ChevronRight className={`h-4 w-4 transform transition-transform ${openModules[module.id] ? 'rotate-90' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="px-4 pb-2">
                    <div className="space-y-1">
                      {module.items.map(item => {
                        const hasAccess = checkMembership(item.accessLevel);
                        
                        return (
                          <div 
                            key={item.id}
                            onClick={() => handleContentClick(module.id, item.id, item.accessLevel)}
                            className={`flex items-center justify-between p-2 rounded-md cursor-pointer ${hasAccess ? 'hover:bg-gray-100' : 'opacity-70'}`}
                          >
                            <div className="flex items-center space-x-2">
                              <div className="text-brand-500">
                                {getContentIcon(item.type)}
                              </div>
                              <div>
                                <p className="text-sm font-medium">{item.title}</p>
                                <p className="text-xs text-gray-500">{item.type === 'video' ? item.duration : 'Document'}</p>
                              </div>
                            </div>
                            
                            {hasAccess ? (
                              <Play className="h-4 w-4 text-brand-500" />
                            ) : (
                              <Lock className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="type">
            <div className="space-y-2">
              <Card>
                <CardHeader className="py-2">
                  <CardTitle className="text-lg">Videos</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {accessibleModules.flatMap(module => 
                    module.items.filter(item => item.type === 'video').map(item => (
                      <div 
                        key={item.id}
                        onClick={() => handleContentClick(module.id, item.id, item.accessLevel)}
                        className={`flex items-center justify-between p-2 rounded-md cursor-pointer ${checkMembership(item.accessLevel) ? 'hover:bg-gray-100' : 'opacity-70'}`}
                      >
                        <div className="flex items-center space-x-2">
                          <Video className="h-4 w-4 text-brand-500" />
                          <div>
                            <p className="text-sm font-medium">{item.title}</p>
                            <p className="text-xs text-gray-500">{module.title} • {item.duration}</p>
                          </div>
                        </div>
                        
                        {checkMembership(item.accessLevel) ? (
                          <Play className="h-4 w-4 text-brand-500" />
                        ) : (
                          <Lock className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="py-2">
                  <CardTitle className="text-lg">Documents</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {accessibleModules.flatMap(module => 
                    module.items.filter(item => item.type === 'pdf').map(item => (
                      <div 
                        key={item.id}
                        onClick={() => handleContentClick(module.id, item.id, item.accessLevel)}
                        className={`flex items-center justify-between p-2 rounded-md cursor-pointer ${checkMembership(item.accessLevel) ? 'hover:bg-gray-100' : 'opacity-70'}`}
                      >
                        <div className="flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-brand-500" />
                          <div>
                            <p className="text-sm font-medium">{item.title}</p>
                            <p className="text-xs text-gray-500">{module.title}</p>
                          </div>
                        </div>
                        
                        {checkMembership(item.accessLevel) ? (
                          <FileText className="h-4 w-4 text-brand-500" />
                        ) : (
                          <Lock className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default CourseContent;
